import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CircleDot, Pencil } from 'lucide-react';
import { CarTireDiagram } from '../components/CarTireDiagram';
import { TireFormModal } from '../components/forms/TireFormModal';
import { Button } from '../components/ui/Button';
import { Card, EmptyState, PageHeader } from '../components/ui/Card';
import { PageLoader } from '../components/ui/Spinner';
import { useCars } from '../hooks/useApi';
import { useUrlFilters } from '../hooks/useUrlFilters';
import type { Car, TireSeason } from '../types';
import { cn, formatDate, isWinterSeason, TIRE_SEASONS } from '../utils/helpers';
import { t } from '../i18n';

const SEASONS = Object.keys(TIRE_SEASONS) as TireSeason[];

export function Tires() {
  const [filters, setFilters] = useUrlFilters({ season: '' });
  const [editing, setEditing] = useState<Car | null>(null);
  const { data, isLoading } = useCars({ page_size: 500 });
  const winter = isWinterSeason();

  if (isLoading) return <PageLoader />;
  const cars = (data?.cars ?? []).filter((c) => c.status === 'active' || c.status === 'service');
  const count = (season: string) => cars.filter((c) => (c.tire_season ?? 'none') === season).length;
  const shown = filters.season ? cars.filter((c) => (c.tire_season ?? 'none') === filters.season) : cars;
  const summerInWinter = winter ? count('summer') : 0;

  const chip = (value: string, label: string, color?: string) => (
    <button
      key={value}
      type="button"
      onClick={() => setFilters({ season: filters.season === value ? '' : value })}
      aria-pressed={filters.season === value}
      className={cn(
        'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors',
        filters.season === value ? 'border-primary-500 bg-primary-50 text-primary-800' : 'border-secondary-200 bg-white text-secondary-700 hover:bg-secondary-50',
      )}
    >
      <span className="h-3 w-3 rounded-full" style={{ backgroundColor: color ?? '#cbd5e1' }} aria-hidden="true" />
      {label}
      <span className="font-semibold tabular-nums">{count(value)}</span>
    </button>
  );

  return (
    <>
      <PageHeader title={t('Pneumatiky')} subtitle={t('Aké pneumatiky má ktoré vozidlo práve namontované')} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {SEASONS.map((s) => chip(s, TIRE_SEASONS[s].label, TIRE_SEASONS[s].color))}
        {chip('none', t('Nezadané'))}
      </div>
      {summerInWinter > 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {t(
            summerInWinter === 1
              ? 'Zimné obdobie: {count} vozidlo má letné pneumatiky. Pri snehu alebo ľade sú na Slovensku povinné zimné.'
              : 'Zimné obdobie: {count} vozidlá majú letné pneumatiky. Pri snehu alebo ľade sú na Slovensku povinné zimné.',
            { count: summerInWinter },
          )}
        </div>
      )}
      {!shown.length ? (
        <Card>
          <EmptyState icon={<CircleDot className="h-10 w-10" />} title={t('Žiadne vozidlá')} />
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {shown.map((car) => {
            const season = car.tire_season ? TIRE_SEASONS[car.tire_season] : null;
            const warn = winter && car.tire_season === 'summer';
            return (
              <Card key={car.id} padding="sm" className={cn('flex gap-4', warn && 'border-amber-300')}>
                <div className="w-24 shrink-0">
                  <CarTireDiagram season={car.tire_season} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <Link to={`/cars/${car.id}`} className="font-mono font-semibold text-primary-700 hover:underline">
                        {car.license_plate}
                      </Link>
                      <p className="truncate text-sm text-secondary-500">
                        {car.brand} {car.model}
                      </p>
                    </div>
                    <button className="icon-btn -mr-1 -mt-1" onClick={() => setEditing(car)} title={t('Upraviť pneumatiky')} aria-label={t('Upraviť pneumatiky {plate}', { plate: car.license_plate })}>
                      <Pencil className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="mt-3 flex items-center gap-2 text-sm font-medium text-secondary-900">
                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: season?.color ?? '#cbd5e1' }} aria-hidden="true" />
                    {season ? `${season.label} pneumatiky` : t('Pneumatiky nezadané')}
                  </p>
                  {car.tire_season ? (
                    <dl className="mt-1 space-y-0.5 text-sm text-secondary-600">
                      <div>{[car.tire_brand, car.tire_model].filter(Boolean).join(' ') || t('Značka nezadaná')}</div>
                      {car.tire_size && <div className="font-mono text-xs">{car.tire_size}</div>}
                      {car.tires_mounted_on && <div className="text-xs text-secondary-500">{t('namontované {date}', { date: formatDate(car.tires_mounted_on) })}</div>}
                    </dl>
                  ) : (
                    <Button size="sm" variant="outline" className="mt-2" onClick={() => setEditing(car)}>
                      {t('Zadať pneumatiky')}
                    </Button>
                  )}
                  {warn && (
                    <p className="mt-2 flex items-center gap-1 text-xs font-medium text-amber-800">
                      <AlertTriangle className="h-3.5 w-3.5" />  {t('letné v zimnom období')}
                    </p>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
      <TireFormModal car={editing} onClose={() => setEditing(null)} />
    </>
  );
}
